import React, { useMemo } from "react";
import PropTypes from "prop-types";
import Tooltip from "@mui/material/Tooltip";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

const DEVICE_META = {
  desktop: { short: "D", label: "Desktop", color: "#3b82f6", empty: "#dbeafe" },
  mobile: { short: "M", label: "Mobile", color: "#10b981", empty: "#d1fae5" },
  tablet: { short: "T", label: "Tablet", color: "#f59e0b", empty: "#fef3c7" },
};
const DEVICE_ORDER = ["desktop", "mobile", "tablet"];

function CompleteDeviceMetric({ metric = null, total: totalFallback = 0 }) {
  const total = Number(metric?.total ?? totalFallback ?? 0);
  const devices = metric?.devices || {};
  const deviceCounts = DEVICE_ORDER.reduce((counts, device) => {
    counts[device] = Number(devices?.[device]?.total || 0);
    return counts;
  }, {});
  const deviceColumnTemplate = DEVICE_ORDER.map((device) => {
    const contentLength = `${DEVICE_META[device].short}:${deviceCounts[device]}`.length;
    return `${Math.max(contentLength, 3)}fr`;
  }).join(" ");
  const browsers = useMemo(() => {
    const totals = {};
    DEVICE_ORDER.forEach((device) => {
      Object.entries(devices?.[device]?.browsers || {}).forEach(([browser, count]) => {
        const name = browser || "Unknown";
        totals[name] = (totals[name] || 0) + Number(count || 0);
      });
    });

    return Object.entries(totals)
      .map(([name, count]) => ({ name, count }))
      .sort((left, right) => right.count - left.count);
  }, [devices]);

  const tooltip = (
    <SoftBox
      sx={{
        minWidth: 190,
        maxWidth: 260,
        p: 1,
        color: "#fff",
        backgroundColor: "#111827",
        borderRadius: "7px",
      }}
    >
      <SoftTypography sx={{ color: "#fff", fontSize: "11px", fontWeight: 600 }}>
        Completes: {total}
      </SoftTypography>
      <SoftBox sx={{ mt: 0.7, pt: 0.7, borderTop: "1px solid rgba(255,255,255,0.16)" }}>
        {browsers.length ? (
          browsers.map((browser) => (
            <SoftBox
              key={browser.name}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1.5}
              mb={0.35}
            >
              <SoftTypography sx={{ color: "#fff", fontSize: "10px" }}>
                {browser.name}
              </SoftTypography>
              <SoftTypography sx={{ color: "#fff", fontSize: "10px", whiteSpace: "nowrap" }}>
                {browser.count} ({total ? ((browser.count / total) * 100).toFixed(1) : "0.0"}%)
              </SoftTypography>
            </SoftBox>
          ))
        ) : (
          <SoftTypography sx={{ color: "#cbd5e1", fontSize: "10px" }}>
            No browser data
          </SoftTypography>
        )}
      </SoftBox>
    </SoftBox>
  );

  return (
    <SoftBox
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "100%",
        minWidth: 0,
        mx: "auto",
        boxSizing: "border-box",
      }}
    >
      <Tooltip
        title={tooltip}
        placement="bottom"
        arrow
        enterDelay={0}
        enterNextDelay={0}
        leaveDelay={0}
        TransitionProps={{ timeout: 0 }}
        componentsProps={{
          tooltip: { sx: { p: 0, maxWidth: "none", backgroundColor: "transparent" } },
          arrow: { sx: { color: "#111827" } },
        }}
      >
        <SoftBox
          sx={{
            display: "grid",
            gridTemplateColumns: deviceColumnTemplate,
            width: "100%",
            minWidth: 0,
            height: 21,
            overflow: "hidden",
            border: "1px solid #d8dee9",
            borderRadius: "5px",
            cursor: "help",
          }}
        >
          {DEVICE_ORDER.map((device, index) => {
            const meta = DEVICE_META[device];
            const count = deviceCounts[device];
            const alert = total > 0 && count / total >= 0.7;

            return (
              <SoftBox
                key={device}
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  color: count ? "#fff" : "#334155",
                  backgroundColor: count ? (alert ? "#e53935" : meta.color) : meta.empty,
                  borderRight: index < 2 ? "1px solid rgba(15,23,42,0.14)" : 0,
                  fontSize: "10px",
                  fontWeight: 600,
                  lineHeight: 1,
                  letterSpacing: "-0.1px",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                  px: "1px",
                }}
              >
                {meta.short}:{count}
              </SoftBox>
            );
          })}
        </SoftBox>
      </Tooltip>
    </SoftBox>
  );
}

CompleteDeviceMetric.propTypes = {
  metric: PropTypes.shape({
    total: PropTypes.number,
    devices: PropTypes.object,
  }),
  total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};


export default CompleteDeviceMetric;
